import { getPasskey, passkeysSupported, type SecondFactors } from '@central/api';
import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useState } from 'react';
import { errorMessage } from './errors';
import type { ReauthOptions } from './reauth';

/** "Confirm it's you": a TOTP code or a passkey, proved on this session (useReauth). */
export function ReauthPrompt({
  opened,
  factors,
  api,
  describeError = errorMessage,
  buttonColor,
  onDone,
  onCancel,
}: ReauthOptions & {
  opened: boolean;
  factors: SecondFactors;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const canPasskey = factors.passkeys > 0 && passkeysSupported();

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
      setCode('');
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const totp = () => run(() => api.reauthTotp({ code: code.replace(/\s/g, '') }));
  const passkey = () =>
    run(async () => {
      const { publicKey } = await api.reauthWebAuthnBegin();
      await api.reauthWebAuthnFinish({ credential: await getPasskey(publicKey) });
    });

  const close = () => {
    setCode('');
    setError(undefined);
    onCancel();
  };

  return (
    <Modal opened={opened} onClose={close} title="Confirm it’s you">
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void totp();
        }}
      >
        <Stack gap={14}>
          <Text size="sm">
            This change needs your second factor, checked in the last 10 minutes.
          </Text>
          {factors.totp && (
            <TextInput
              label="Code from your authenticator app"
              inputMode="numeric"
              autoComplete="one-time-code"
              data-autofocus
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
            />
          )}
          {!factors.totp && !canPasskey && (
            <Text size="sm" c="red">
              This browser cannot use your passkey. Try again from one that can.
            </Text>
          )}
          {error !== undefined && <Alert color="red">{describeError(error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            {canPasskey && (
              <Button
                variant={factors.totp ? 'default' : 'filled'}
                color={buttonColor}
                loading={busy}
                onClick={() => void passkey()}
              >
                Use a passkey
              </Button>
            )}
            {factors.totp && (
              <Button type="submit" color={buttonColor} loading={busy}>
                Confirm
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
